/**
 * Main application layout with collapsible sidebar navigation.
 *
 * @module components/layout/Layout
 */

import { ReactNode, useState, useMemo, useCallback } from 'react';
import { Link, useLocation, useNavigate } from 'react-router-dom';
import { clsx } from 'clsx';
import { SandboxGroupSelector } from './SandboxGroupSelector';
import {
  HardDrive,
  Box,
  Settings,
  Menu,
  X,
  Server,
  Camera,
  Database,
  Moon,
  Sun,
  LogOut,
  Plug,
  Cable,
  AppWindow,
  Terminal,
  UserCog,
  Search,
  Package,
  KeyRound,
  FolderGit2,
  Bug,
  BarChart3,
  BookOpen,
} from 'lucide-react';
import { useDarkMode, usePreviewMode } from '@/hooks';
import { useAuth } from '@/contexts/AuthContext';
import { ErrorBadge, ErrorDrawer, BugReportPanel } from '@/components/ui';
import { useBugReportStore } from '@/stores/useBugReportStore';
import { PreviewBanner, PREVIEW_BANNER_HEIGHT } from './PreviewBanner';
import { GitHubLinkBanner, GITHUB_LINK_BANNER_HEIGHT } from './GitHubLinkBanner';
import { config } from '@/config';

const SIDEBAR_STORAGE_KEY = 'adc_sidebar_collapsed';
const DISMISSED_BADGES_KEY = 'adc_dismissed_badges';

interface LayoutProps {
  children: ReactNode;
}

interface NavItem {
  name: string;
  href: string;
  icon: ReactNode;
  previewOnly?: boolean;
  /** localStorage key for a "New" badge; badge shown until user clicks the item. */
  newBadgeKey?: string;
}

/** Resources that have a group-scoped route variant. */
const GROUP_SCOPEABLE = new Set([
  '/sandboxes',
  '/disk-images',
  '/snapshots',
  '/secrets',
  '/volumes',
  '/connectors',
  '/content-packages',
]);

const allNavItems: NavItem[] = [
  {
    name: 'Sandboxes',
    href: '/sandboxes',
    icon: <Box className="h-4 w-4" />,
  },
  {
    name: 'Apps',
    href: '/apps',
    icon: <AppWindow className="h-4 w-4" />,
    previewOnly: true,
  },
  {
    name: 'Disk Images',
    href: '/disk-images',
    icon: <HardDrive className="h-4 w-4" />,
  },
  {
    name: 'Snapshots',
    href: '/snapshots',
    icon: <Camera className="h-4 w-4" />,
  },
  {
    name: 'Volumes',
    href: '/volumes',
    icon: <Database className="h-4 w-4" />,
  },
  {
    name: 'Secrets',
    href: '/secrets',
    icon: <KeyRound className="h-4 w-4" />,
  },
  {
    name: 'Projects',
    href: '/projects',
    icon: <FolderGit2 className="h-4 w-4" />,
  },
  {
    name: 'Agent IDs',
    href: '/agent-identities',
    icon: <UserCog className="h-4 w-4" />,
    previewOnly: true,
  },
  {
    name: 'Connectors',
    href: '/connectors',
    icon: <Cable className="h-4 w-4" />,
  },
  {
    name: 'Monitoring',
    href: '/monitoring',
    icon: <BarChart3 className="h-4 w-4" />,
    newBadgeKey: 'monitoring',
  },
  {
    name: 'Debug Logs',
    href: '/logs',
    icon: <Search className="h-4 w-4" />,
  },
  {
    name: 'Packages',
    href: '/content-packages',
    icon: <Package className="h-4 w-4" />,
  },
  {
    name: 'MCP',
    href: '/mcp',
    icon: <Plug className="h-4 w-4" />,
  },
  {
    name: 'CLI + SDKs',
    href: '/getting-started',
    icon: <Terminal className="h-4 w-4" />,
  },
  {
    name: 'Docs',
    href: '/docs',
    icon: <BookOpen className="h-4 w-4" />,
  },
  {
    name: 'Settings',
    href: '/settings',
    icon: <Settings className="h-4 w-4" />,
  },
];

/**
 * Main layout component with collapsible sidebar navigation
 */
export function Layout({ children }: LayoutProps) {
  const [sidebarOpen, setSidebarOpen] = useState(false);
  const [sidebarCollapsed, setSidebarCollapsed] = useState(() => {
    try {
      return localStorage.getItem(SIDEBAR_STORAGE_KEY) === 'true';
    } catch {
      return false;
    }
  });

  // Track which "New" badges the user has dismissed
  const [dismissedBadges, setDismissedBadges] = useState<Set<string>>(() => {
    try {
      const stored = localStorage.getItem(DISMISSED_BADGES_KEY);
      return stored ? new Set(JSON.parse(stored) as string[]) : new Set();
    } catch {
      return new Set();
    }
  });

  const dismissBadge = useCallback((key: string) => {
    setDismissedBadges((prev) => {
      const next = new Set(prev).add(key);
      try {
        localStorage.setItem(DISMISSED_BADGES_KEY, JSON.stringify([...next]));
      } catch {
        /* localStorage may be unavailable */
      }
      return next;
    });
  }, []);
  const [isDarkMode, toggleDarkMode] = useDarkMode();
  const isPreviewMode = usePreviewMode();
  const location = useLocation();
  const navigate = useNavigate();
  const { user, logout } = useAuth();
  const { openPanel: openBugReportPanel } = useBugReportStore();

  // Get current sandbox ID from URL if on sandbox detail page
  const currentSandboxId = useMemo(() => {
    const match = location.pathname.match(/^\/sandboxes\/([^/]+)$/);
    return match ? match[1] : undefined;
  }, [location.pathname]);

  // Get current trace ID from URL if on trace detail page
  const currentTraceId = useMemo(() => {
    const match = location.pathname.match(/^\/logs\/traces\/([^/]+)$/);
    return match ? match[1] : undefined;
  }, [location.pathname]);

  // Handle opening bug report with context
  const handleOpenBugReport = useCallback(() => {
    openBugReportPanel({
      sandboxId: currentSandboxId,
      traceId: currentTraceId,
    });
    setSidebarOpen(false);
  }, [openBugReportPanel, currentSandboxId, currentTraceId]);

  // Compute the active sandbox group from the current URL
  const activeGroupId = useMemo(() => {
    const match = location.pathname.match(/^\/sandbox-groups\/([^/]+)/);
    return match ? decodeURIComponent(match[1]) : null;
  }, [location.pathname]);

  // Filter nav items based on preview mode and group scope
  const navItems = useMemo(
    () =>
      allNavItems.filter(
        (item) =>
          (!item.previewOnly || isPreviewMode) && (!activeGroupId || GROUP_SCOPEABLE.has(item.href))
      ),
    [isPreviewMode, activeGroupId]
  );

  /** Compute the effective href for a nav item — group-scoped when a group is active. */
  const effectiveHref = useCallback(
    (baseHref: string) => {
      if (!activeGroupId || !GROUP_SCOPEABLE.has(baseHref)) return baseHref;
      const segment = baseHref.replace('/', '');
      // disk-images stays as-is for the segment
      return `/sandbox-groups/${encodeURIComponent(activeGroupId)}/${segment}`;
    },
    [activeGroupId]
  );

  const toggleSidebarCollapsed = useCallback(() => {
    setSidebarCollapsed((prev) => {
      const next = !prev;
      try {
        localStorage.setItem(SIDEBAR_STORAGE_KEY, String(next));
      } catch {
        // localStorage may be unavailable
      }
      return next;
    });
  }, []);

  const handleLogout = () => {
    logout();
    navigate('/login');
  };

  const isActive = (href: string) => {
    const path = location.pathname;
    if (path === href || path.startsWith(href + '/')) return true;
    // Also highlight nav item when on the group-scoped variant of the same resource
    if (href === '/sandboxes' && path.match(/^\/sandbox-groups\/[^/]+\/sandboxes/)) return true;
    if (href === '/disk-images' && path.match(/^\/sandbox-groups\/[^/]+\/disk-images/)) return true;
    if (href === '/snapshots' && path.match(/^\/sandbox-groups\/[^/]+\/snapshots/)) return true;
    if (href === '/secrets' && path.match(/^\/sandbox-groups\/[^/]+\/secrets/)) return true;
    if (href === '/volumes' && path.match(/^\/sandbox-groups\/[^/]+\/volumes/)) return true;
    if (href === '/connectors' && path.match(/^\/sandbox-groups\/[^/]+\/connectors/)) return true;
    if (href === '/content-packages' && path.match(/^\/sandbox-groups\/[^/]+\/content-packages/))
      return true;
    return false;
  };

  // Check if preview banner is visible (has stable URL to link to)
  const showPreviewBanner = config.isPreviewMode && config.stablePortalUrl;
  // GitHub link banner is self-managing (checks auth type + copilot status internally)
  // We need to account for its height in layout offsets when it's visible
  const [showGitHubBanner, setShowGitHubBanner] = useState(false);
  const bannerOffset =
    (showPreviewBanner ? PREVIEW_BANNER_HEIGHT : 0) +
    (showGitHubBanner ? GITHUB_LINK_BANNER_HEIGHT : 0);

  return (
    <div className="min-h-screen bg-white dark:bg-[#0a0a0a]">
      {/* Preview mode banner */}
      <PreviewBanner />
      {/* GitHub link banner for Entra ID users without copilot connection — stacked below preview banner */}
      <div
        className="fixed left-0 right-0 z-[59]"
        style={{ top: showPreviewBanner ? PREVIEW_BANNER_HEIGHT : 0 }}
      >
        <GitHubLinkBanner onVisibilityChange={setShowGitHubBanner} />
      </div>

      {/* Mobile sidebar backdrop */}
      {sidebarOpen && (
        <div
          className="fixed inset-0 z-40 bg-black/50 lg:hidden"
          style={bannerOffset > 0 ? { top: bannerOffset } : undefined}
          onClick={() => setSidebarOpen(false)}
        />
      )}

      {/* Sidebar */}
      <aside
        className={clsx(
          'fixed left-0 bottom-0 z-50 bg-white dark:bg-[#161616] border-r border-gray-200 dark:border-[#2a2a2a] transform transition-all duration-200 ease-in-out lg:translate-x-0 flex flex-col',
          sidebarOpen ? 'translate-x-0' : '-translate-x-full',
          sidebarCollapsed ? 'lg:w-14' : 'lg:w-56',
          // On mobile, always show full width
          'w-56'
        )}
        style={{ top: bannerOffset }}
      >
        {/* Logo */}
        <div className="flex items-center h-16 px-4 border-b border-gray-200 dark:border-[#2a2a2a]">
          <button
            type="button"
            onClick={toggleSidebarCollapsed}
            title={sidebarCollapsed ? 'Expand sidebar' : 'Collapse sidebar'}
            aria-label={sidebarCollapsed ? 'Expand sidebar' : 'Collapse sidebar'}
            className="hidden lg:flex items-center gap-2 hover:opacity-80 transition-opacity"
          >
            <div className="flex items-center justify-center w-8 h-8 flex-shrink-0">
              <Server className="h-5 w-5 text-gray-900 dark:text-white" />
            </div>
            {!sidebarCollapsed && (
              <h1 className="font-semibold text-gray-900 dark:text-white text-base">ADC</h1>
            )}
          </button>
          {/* Mobile: link to home */}
          <Link
            to="/"
            className="flex lg:hidden items-center gap-2 hover:opacity-80 transition-opacity"
          >
            <div className="flex items-center justify-center w-8 h-8 flex-shrink-0">
              <Server className="h-5 w-5 text-gray-900 dark:text-white" />
            </div>
            <h1 className="font-semibold text-gray-900 dark:text-white text-base">ADC</h1>
          </Link>
          {/* Mobile close button */}
          <button
            className="ml-auto lg:hidden rounded-lg p-1 text-gray-400 hover:text-gray-500 hover:bg-gray-100"
            onClick={() => setSidebarOpen(false)}
            aria-label="Close navigation menu"
          >
            <X className="h-4 w-4" />
          </button>
        </div>

        {/* Navigation */}
        <nav
          aria-label="Main navigation"
          className={clsx(
            'flex-1 py-3 space-y-0.5 overflow-y-auto',
            sidebarCollapsed ? 'px-1.5' : 'px-3'
          )}
        >
          {navItems.map((item) => (
            <Link
              key={item.href}
              to={effectiveHref(item.href)}
              title={sidebarCollapsed ? item.name : undefined}
              className={clsx(
                'flex items-center rounded-md text-sm font-normal transition-all duration-150',
                sidebarCollapsed ? 'justify-center px-0 py-2' : 'gap-3 px-3 py-2',
                isActive(item.href)
                  ? 'bg-gray-100 dark:bg-[#2a2a2a] text-gray-900 dark:text-white'
                  : 'text-gray-600 dark:text-gray-400 hover:bg-gray-50 dark:hover:bg-[#2a2a2a]/50 hover:text-gray-900 dark:hover:text-gray-200'
              )}
              onClick={() => {
                setSidebarOpen(false);
                if (item.newBadgeKey && !dismissedBadges.has(item.newBadgeKey)) {
                  dismissBadge(item.newBadgeKey);
                }
              }}
            >
              <span
                className={clsx(
                  'flex-shrink-0',
                  isActive(item.href)
                    ? 'text-gray-700 dark:text-gray-300'
                    : 'text-gray-400 dark:text-gray-400'
                )}
              >
                {item.icon}
              </span>
              {!sidebarCollapsed && (
                <>
                  {item.name}
                  {item.newBadgeKey && !dismissedBadges.has(item.newBadgeKey) && (
                    <span className="ml-auto text-[10px] font-semibold uppercase tracking-wide bg-blue-500 text-white px-1.5 py-0.5 rounded-full leading-none">
                      New
                    </span>
                  )}
                </>
              )}
            </Link>
          ))}
        </nav>

        {/* Bottom section */}
        <div
          className={clsx(
            'border-t border-gray-200 dark:border-[#2a2a2a] space-y-1',
            sidebarCollapsed ? 'p-1.5' : 'p-3'
          )}
        >
          <button
            type="button"
            onClick={handleOpenBugReport}
            title={sidebarCollapsed ? 'Bug Report' : undefined}
            className={clsx(
              'flex items-center rounded-md text-sm text-gray-600 dark:text-gray-400 hover:bg-gray-50 dark:hover:bg-[#2a2a2a] hover:text-gray-900 dark:hover:text-gray-200 transition-colors w-full',
              sidebarCollapsed ? 'justify-center px-0 py-2' : 'gap-3 px-3 py-2 text-left'
            )}
          >
            <Bug className="h-4 w-4 text-gray-400 dark:text-gray-400" />
            {!sidebarCollapsed && 'Bug Report'}
          </button>
          <button
            type="button"
            onClick={toggleDarkMode}
            title={sidebarCollapsed ? (isDarkMode ? 'Light mode' : 'Dark mode') : undefined}
            className={clsx(
              'flex items-center rounded-md text-sm text-gray-600 dark:text-gray-400 hover:bg-gray-50 dark:hover:bg-[#2a2a2a] hover:text-gray-900 dark:hover:text-gray-200 transition-colors w-full',
              sidebarCollapsed ? 'justify-center px-0 py-2' : 'gap-3 px-3 py-2 text-left'
            )}
          >
            {isDarkMode ? (
              <Sun className="h-4 w-4 text-gray-400 dark:text-gray-400" />
            ) : (
              <Moon className="h-4 w-4 text-gray-400 dark:text-gray-400" />
            )}
            {!sidebarCollapsed && (isDarkMode ? 'Light mode' : 'Dark mode')}
          </button>
          <button
            type="button"
            onClick={handleLogout}
            title={sidebarCollapsed ? 'Log out' : undefined}
            className={clsx(
              'flex items-center rounded-md text-sm text-gray-600 dark:text-gray-400 hover:bg-gray-50 dark:hover:bg-[#2a2a2a] hover:text-gray-900 dark:hover:text-gray-200 transition-colors w-full',
              sidebarCollapsed ? 'justify-center px-0 py-2' : 'gap-3 px-3 py-2 text-left'
            )}
          >
            <LogOut className="h-4 w-4 text-gray-400 dark:text-gray-400" />
            {!sidebarCollapsed && 'Log out'}
          </button>
        </div>

        {/* User section */}
        <div
          className={clsx(
            'border-t border-gray-200 dark:border-[#2a2a2a]',
            sidebarCollapsed ? 'p-1.5' : 'p-3'
          )}
        >
          <div
            className={clsx(
              'flex items-center',
              sidebarCollapsed ? 'justify-center py-2' : 'gap-2 px-3 py-2'
            )}
          >
            <div className="w-8 h-8 rounded-full bg-gray-200 dark:bg-[#2a2a2a] flex items-center justify-center overflow-hidden flex-shrink-0">
              {user?.authType === 'entra_id' ? (
                <svg viewBox="0 0 23 23" className="w-5 h-5" xmlns="http://www.w3.org/2000/svg">
                  <rect x="1" y="1" width="10" height="10" fill="#f25022" />
                  <rect x="12" y="1" width="10" height="10" fill="#7fba00" />
                  <rect x="1" y="12" width="10" height="10" fill="#00a4ef" />
                  <rect x="12" y="12" width="10" height="10" fill="#ffb900" />
                </svg>
              ) : user?.avatar_url ? (
                <img
                  src={user.avatar_url}
                  alt={user.login}
                  className="w-full h-full object-cover"
                />
              ) : (
                <span className="text-xs font-medium text-gray-600 dark:text-gray-400">
                  {user?.login?.[0]?.toUpperCase() || 'U'}
                </span>
              )}
            </div>
            {!sidebarCollapsed && (
              <div className="flex-1 min-w-0">
                <div className="flex items-center gap-2">
                  <p className="text-sm font-medium text-gray-900 dark:text-white truncate">
                    {user?.name || user?.login || 'User'}
                  </p>
                </div>
                <p className="text-xs text-gray-500 dark:text-gray-400">@{user?.login || 'user'}</p>
              </div>
            )}
          </div>
        </div>
      </aside>

      {/* Main content */}
      <div
        className={clsx('transition-all duration-200', sidebarCollapsed ? 'lg:pl-14' : 'lg:pl-56')}
        style={bannerOffset > 0 ? { paddingTop: bannerOffset } : undefined}
      >
        {/* Top bar */}
        <header
          className="sticky z-30 flex items-center h-16 px-4 bg-white dark:bg-[#0a0a0a] border-b border-gray-200 dark:border-[#2a2a2a] lg:px-8"
          style={{ top: bannerOffset }}
        >
          <button
            className="lg:hidden rounded-lg p-2 text-gray-400 hover:text-gray-500 hover:bg-gray-100"
            onClick={() => setSidebarOpen(true)}
            aria-label="Open navigation menu"
          >
            <Menu className="h-5 w-5" />
          </button>
          {/* Sandbox group scope selector */}
          <SandboxGroupSelector />
          <div className="flex-1" />
          {/* Error history badge */}
          <ErrorBadge />
        </header>

        {/* Error drawer (portal) */}
        <ErrorDrawer />

        {/* Bug report panel */}
        <BugReportPanel />

        {/* Page content */}
        <main className="p-6 lg:p-8">{children}</main>
      </div>
    </div>
  );
}
